import { Fragment, useEffect, useRef } from 'react';
import { formatDateTime } from '../../../../shared/src';
import type { AuditLogEntry } from '../../api';
import { CATEGORY_LABELS, detailEntries, eventLabel, projectLabel } from '../../lib/auditEvents';
import { AuditActor } from './AuditActor';

interface AuditLogDetailsPanelProps {
  log: AuditLogEntry;
  onClose: () => void;
}

/**
 * Slide-in panel with everything Admin > Audit Logs shows of one event,
 * including the details the table has no column for. Clicking the backdrop
 * or pressing Escape closes it.
 */
export function AuditLogDetailsPanel({ log, onClose }: AuditLogDetailsPanelProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const details = detailEntries(log);

  return (
    <div className="side-panel-overlay" onClick={onClose}>
      <aside
        className="side-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-log-panel-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="side-panel-header">
          <div>
            <h2 id="audit-log-panel-title">{eventLabel(log.event)}</h2>
            <div className="side-panel-subtitle">{formatDateTime(log.created_at, { withZone: true })}</div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="side-panel-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="side-panel-body">
          <dl className="side-panel-grid">
            <dt>Time (UTC)</dt>
            <dd>{new Date(log.created_at).toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC')}</dd>

            <dt>User</dt>
            <dd>
              <AuditActor log={log} />
              {log.actor.type === 'user' && log.actor.name && <span className="side-panel-muted">{log.actor.name}</span>}
            </dd>

            <dt>Category</dt>
            <dd>{CATEGORY_LABELS[log.category] ?? log.category}</dd>

            <dt>Project</dt>
            <dd>{log.project ? projectLabel(log.project) : <span className="side-panel-muted">-</span>}</dd>

            {log.user_agent && (
              <>
                <dt>Browser</dt>
                <dd className="side-panel-muted">{log.user_agent}</dd>
              </>
            )}

            <dt>Event ID</dt>
            <dd>
              <code>{log.id}</code>
            </dd>
          </dl>

          {log.actor.type === 'fakto_support' && (
            <p className="side-panel-note">
              Done by Fakto&apos;s support team in your organization.
            </p>
          )}

          <h3>Details</h3>
          {details.length === 0 ? (
            <p className="side-panel-muted">No details recorded</p>
          ) : (
            <dl className="side-panel-grid">
              {details.map(([label, text]) => (
                <Fragment key={label}>
                  <dt>{label}</dt>
                  <dd>{text}</dd>
                </Fragment>
              ))}
            </dl>
          )}
        </div>
      </aside>
    </div>
  );
}
